import { useAuthStore } from '@/stores/authStore';
import { NoteGoApiContext } from './noteGoApiContext';
import { logger } from '@/services/loggerService';

const baseUrl = import.meta.env.VITE_API_URL;

export type ErrorWrapper<TError> = TError | { status: 'unknown'; payload: string };

export type NoteGoApiFetcherOptions<TBody, THeaders, TQueryParams, TPathParams> = {
  url: string;
  method: string;
  body?: TBody;
  headers?: THeaders;
  queryParams?: TQueryParams;
  pathParams?: TPathParams;
  signal?: AbortSignal;
} & NoteGoApiContext['fetcherOptions'];

export async function noteGoApiFetch<
  TData,
  TError,
  TBody extends {} | FormData | undefined | null,
  THeaders extends {},
  TQueryParams extends {},
  TPathParams extends {},
>({
  url,
  method,
  body,
  headers,
  pathParams,
  queryParams,
  signal,
}: NoteGoApiFetcherOptions<TBody, THeaders, TQueryParams, TPathParams>): Promise<TData> {
  try {
    const { userToken, setUserToken } = useAuthStore.getState();

    // Add the token to the headers
    const requestHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      ...headers,
    };

    // Only add authorization header if we have a valid token
    if (userToken) {
      requestHeaders.authorization = `Bearer ${userToken}`;
    }

    // Delete Content-Type if it is multipart/form-data
    if (requestHeaders['Content-Type'].toLowerCase().includes('multipart/form-data')) {
      delete requestHeaders['Content-Type'];
    }

    const requestUrl = `${baseUrl}${resolveUrl(url, queryParams, pathParams)}`;
    logger.debug('api', `Request: ${method.toUpperCase()} ${requestUrl}`);

    const response = await window.fetch(requestUrl, {
      signal,
      method: method.toUpperCase(),
      body: body ? (body instanceof FormData ? body : JSON.stringify(body)) : undefined,
      headers: requestHeaders,
    });

    logger.debug('api', `Response: ${response.status} ${requestUrl}`);

    if (!response.ok) {
      if (response.status === 401) {
        // Import auth to get current user directly from Firebase
        const { auth } = await import('@/firebase/firebase');
        const firebaseUser = auth.currentUser;

        if (firebaseUser) {
          try {
            // If the response is unauthorized and the user is logged in, try to renew the token
            const newToken = await firebaseUser.getIdToken(true);
            setUserToken(newToken);

            requestHeaders.authorization = `Bearer ${newToken}`;
            const retryResponse = await window.fetch(`${baseUrl}${resolveUrl(url, queryParams, pathParams)}`, {
              signal,
              method: method.toUpperCase(),
              body: body ? (body instanceof FormData ? body : JSON.stringify(body)) : undefined,
              headers: requestHeaders,
            });

            if (!retryResponse.ok) {
              throw await retryResponse.json();
            }

            return retryResponse.headers.get('content-type')?.includes('json')
              ? await retryResponse.json()
              : ((await retryResponse.blob()) as unknown as TData);
          } catch (tokenError) {
            throw tokenError;
          }
        } else {
          // Wait for Firebase auth to be ready
          const { onAuthStateChanged } = await import('firebase/auth');
          return new Promise((resolve, reject) => {
            const unsubscribe = onAuthStateChanged(auth, async (user) => {
              unsubscribe();
              if (user) {
                try {
                  const newToken = await user.getIdToken(true);
                  setUserToken(newToken);

                  requestHeaders.authorization = `Bearer ${newToken}`;
                  const retryResponse = await window.fetch(`${baseUrl}${resolveUrl(url, queryParams, pathParams)}`, {
                    signal,
                    method: method.toUpperCase(),
                    body: body ? (body instanceof FormData ? body : JSON.stringify(body)) : undefined,
                    headers: requestHeaders,
                  });

                  if (!retryResponse.ok) {
                    reject(await retryResponse.json());
                    return;
                  }

                  resolve(
                    retryResponse.headers.get('content-type')?.includes('json')
                      ? await retryResponse.json()
                      : ((await retryResponse.blob()) as unknown as TData),
                  );
                } catch (error) {
                  reject(error);
                }
              } else {
                reject(new Error('User not authenticated'));
              }
            });
          });
        }
      }

      let error: ErrorWrapper<TError>;
      try {
        error = await response.json();
        // Attach the numeric HTTP status so callers can branch on it (e.g. 403).
        // FastAPI error bodies are { detail } and carry no status otherwise.
        if (error && typeof error === 'object') {
          (error as { status?: number }).status = response.status;
        }
      } catch (e) {
        error = {
          status: 'unknown' as const,
          payload: e instanceof Error ? `Unexpected error (${e.message})` : 'Unexpected error',
        };
      }
      throw error;
    }

    if (response.status === 204) {
      return null as unknown as TData;
    }

    return response.headers.get('content-type')?.includes('json')
      ? await response.json()
      : ((await response.blob()) as unknown as TData);
  } catch (e) {
    // API errors (from response.json()) are plain objects — re-throw as-is
    // so callers can inspect .detail, .status, etc.
    // Only wrap actual network/fetch errors (TypeError, DOMException).
    if (!(e instanceof Error)) {
      throw e;
    }
    logger.warn('api', 'Network error caught', e);
    const errorObject: Error = {
      name: 'unknown' as const,
      message: `Network error (${e.message})`,
      stack: e.stack || '',
    };
    throw errorObject;
  }
}

const resolveUrl = (url: string, queryParams: Record<string, string> = {}, pathParams: Record<string, string> = {}) => {
  let query = new URLSearchParams(queryParams).toString();
  if (query) query = `?${query}`;
  return url.replace(/\{\w*\}/g, (key) => pathParams[key.slice(1, -1)]) + query;
};
